import { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { Tile } from '@/components/primitives/Tile';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useViewsEvolution } from '@/hooks/queries/useViewsEvolution';
import { useFollowersEvolution } from '@/hooks/queries/useFollowersEvolution';
import { cn } from '@/lib/cn';
import {
  formatAnalyticsBucket,
  formatAnalyticsValue,
  prepareAnalyticsSeries,
} from '@/lib/analyticsSeries';
import { LineSeriesChart } from './LineSeriesChart';
import type { Granularity } from '@/types/enums';
import type { IdentityResponse, OverviewSeriesResponse, TimeSeries } from '@/types/api';
import type { AnalyticsSeriesMode, AnalyticsSeriesPoint } from '@/lib/analyticsSeries';

interface EvolutionSectionProps {
  periodDays: number;
  granularity: Granularity;
  onGranularityChange: (granularity: Granularity) => void;
  identityId: string | undefined;
  onIdentityChange: (identityId: string) => void;
  identities: IdentityResponse[] | undefined;
  series: OverviewSeriesResponse | undefined;
  loading: boolean;
  referenceTime?: number;
  error?: unknown;
  onRetry?: () => void;
}

const VIOLET = 'hsl(var(--violet))';
const EMERALD = 'hsl(var(--emerald))';

export function EvolutionSection({
  periodDays,
  granularity,
  onGranularityChange,
  identityId,
  onIdentityChange,
  identities,
  series,
  loading,
  referenceTime,
  error,
  onRetry,
}: EvolutionSectionProps) {
  const { t, locale } = useI18n();
  const [metric, setMetric] = useState<'views' | 'followers'>('views');
  const [mode, setMode] = useState<AnalyticsSeriesMode>('bucket');
  const [mountedAt] = useState(() => Date.now());
  const now = referenceTime ?? mountedAt;
  const fromIso = new Date(periodDays === 0 ? 0 : now - periodDays * 86_400_000).toISOString();
  const toIso = new Date(now).toISOString();
  const viewsDrill = useViewsEvolution(
    { identityId, from: fromIso, to: toIso, granularity },
    { enabled: Boolean(identityId) },
  );
  const followersDrill = useFollowersEvolution(
    { identityId, from: fromIso, to: toIso, granularity },
    { enabled: Boolean(identityId) },
  );
  const drill = metric === 'views' ? viewsDrill : followersDrill;
  const data = useMemo(
    () =>
      identityId
        ? mapTimeSeries(drill.data)
        : mapOverviewSeries(metric === 'views' ? series?.viewsDelta : series?.followers),
    [identityId, drill.data, metric, series],
  );
  const chartData = useMemo(
    () => prepareAnalyticsSeries(data, granularity, 'bucket', now),
    [data, granularity, now],
  );
  const measured = chartData.filter((point) => point.v !== null);
  const total = measured.reduce((sum, point) => sum + (point.v ?? 0), 0);
  const latest = measured.at(-1);
  const summaryValue = metric === 'views' ? (measured.length ? total : null) : latest?.v;
  const isLoading = identityId ? drill.isLoading : loading;
  const chartError = identityId ? drill.error : error;
  const hasGaps = chartData.some((point) => point.v === null);
  const isPartial = chartData.some((point) => point.partial);
  const bucketLabel = {
    HOUR: t('Par heure', 'Hourly'),
    DAY: t('Par jour', 'Daily'),
    WEEK: t('Par semaine', 'Weekly'),
    MONTH: t('Par mois', 'Monthly'),
  }[granularity];
  const metricTitle =
    metric === 'views'
      ? t('Vues gagnées', 'Views gained')
      : t('Abonnés actuels', 'Current followers');
  const valueLabel =
    metric === 'views' && mode === 'cumulative'
      ? t('Vues cumulées', 'Cumulative views')
      : metricTitle;
  const selectionClass = (selected: boolean) =>
    cn(
      'min-h-11 min-w-0 rounded-md px-2.5 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet',
      selected ? 'bg-bg-1 text-text-1 shadow-sm' : 'text-text-3 hover:text-text-1',
    );

  return (
    <section
      id="analytics-evolution"
      className="min-w-0 scroll-mt-24"
      aria-label={t('Évolution', 'Trends')}
    >
      <Tile className="rounded-2xl px-4 pt-4 sm:px-5 sm:pt-5">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex min-w-0 items-center gap-3">
            <h3 className="text-[15px] font-semibold tracking-tight text-text-1">
              {t('Évolution', 'Trends')}
            </h3>
            <div
              className="flex max-w-full rounded-lg bg-bg-2 p-0.5"
              role="group"
              aria-label={t('Indicateur du graphique', 'Chart metric')}
            >
              <button
                type="button"
                className={selectionClass(metric === 'views')}
                aria-pressed={metric === 'views'}
                onClick={() => setMetric('views')}
              >
                {t('Vues', 'Views')}
              </button>
              <button
                type="button"
                className={selectionClass(metric === 'followers')}
                aria-pressed={metric === 'followers'}
                onClick={() => setMetric('followers')}
              >
                {t('Abonnés actuels', 'Current followers')}
              </button>
            </div>
          </div>
          <div className="grid w-full min-w-0 grid-cols-2 gap-2 sm:w-[300px]">
            <Select value={identityId ?? 'all'} onValueChange={onIdentityChange}>
              <SelectTrigger
                className="h-11 px-2.5 text-[12px] sm:h-11"
                aria-label={t('Identité du graphique', 'Chart identity')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('Toute la flotte', 'Whole fleet')}</SelectItem>
                {(identities ?? []).map((identity) => (
                  <SelectItem key={identity.id} value={identity.id}>
                    {identity.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={granularity}
              onValueChange={(value) => onGranularityChange(value as Granularity)}
            >
              <SelectTrigger
                className="h-11 px-2.5 text-[12px] sm:h-11"
                aria-label={t('Regroupement du graphique', 'Chart grouping')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="HOUR" disabled={periodDays === 0 || periodDays > 14}>
                  {t('Par heure', 'Hourly')}
                  {periodDays === 0 || periodDays > 14 ? t(' (≤ 14 j)', ' (≤ 14 d)') : ''}
                </SelectItem>
                <SelectItem value="DAY">{t('Par jour', 'Daily')}</SelectItem>
                <SelectItem value="WEEK">{t('Par semaine', 'Weekly')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <div className="flex min-w-0 flex-col items-start gap-x-2 sm:flex-row sm:flex-wrap sm:items-baseline">
            <h4 className="sr-only">{metricTitle}</h4>
            <p
              className="text-[21px] font-semibold tabular-nums leading-8 tracking-tight text-text-1"
              aria-live="polite"
            >
              {isLoading || chartError || summaryValue === null || summaryValue === undefined
                ? '—'
                : formatAnalyticsValue(summaryValue, locale)}
            </p>
            <span className="text-[10px] text-text-3">
              {metric === 'views'
                ? t('total sur la période', 'total over the period')
                : t('dernière mesure', 'latest observation')}
            </span>
          </div>
          {metric === 'views' && (
            <div
              className="flex max-w-full rounded-lg bg-bg-2 p-0.5"
              role="group"
              aria-label={t('Affichage des vues', 'Views display')}
            >
              <button
                type="button"
                className={selectionClass(mode === 'bucket')}
                aria-pressed={mode === 'bucket'}
                onClick={() => setMode('bucket')}
              >
                {bucketLabel}
              </button>
              <button
                type="button"
                className={selectionClass(mode === 'cumulative')}
                aria-pressed={mode === 'cumulative'}
                onClick={() => setMode('cumulative')}
              >
                {t('Cumul', 'Cumulative')}
              </button>
            </div>
          )}
        </div>
        {chartError ? (
          <ErrorInline
            error={chartError}
            className="my-3"
            onRetry={
              identityId
                ? () => {
                    void drill.refetch();
                  }
                : onRetry
            }
          />
        ) : (
          <LineSeriesChart
            data={data}
            color={metric === 'views' ? EMERALD : VIOLET}
            granularity={granularity}
            variant={metric === 'views' && mode === 'bucket' ? 'bar' : 'line'}
            mode={metric === 'views' ? mode : 'bucket'}
            valueLabel={valueLabel}
            loading={isLoading}
            ariaLabel={`${valueLabel} · ${bucketLabel} · UTC`}
            now={now}
          />
        )}
        <div className="flex min-h-11 items-center justify-between gap-2 border-t border-line-1 text-[10px] text-text-3">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 py-1">
            <span>
              {measured.length > 0 && !chartError && !isLoading ? (
                <>
                  {formatAnalyticsBucket(measured[0].x, granularity, locale)}
                  {' – '}
                  {formatAnalyticsBucket(measured[measured.length - 1].x, granularity, locale)}
                  {' · UTC'}
                </>
              ) : (
                'UTC'
              )}
            </span>
            {isPartial && (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-amber" />
                {t('Période en cours partielle', 'Current bucket is partial')}
              </span>
            )}
          </div>
          <details className="relative shrink-0">
            <summary
              className="flex size-11 cursor-pointer list-none items-center justify-center rounded-lg hover:bg-bg-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet [&::-webkit-details-marker]:hidden"
              aria-label={t('Comprendre le graphique', 'Understand this chart')}
            >
              <Info className="h-4 w-4" aria-hidden />
            </summary>
            <div className="absolute bottom-12 right-0 z-10 w-[270px] max-w-[calc(100vw-4rem)] space-y-2 rounded-[10px] border border-line-1 bg-bg-1 p-3 text-[12px] leading-relaxed text-text-2 shadow-card">
              <p>
                {metric === 'views'
                  ? mode === 'bucket'
                    ? t(
                        'Le total additionne les vues gagnées sur la période. Chaque barre montre uniquement les vues gagnées pendant son jour, sa semaine ou son heure.',
                        'The total adds up views gained over the selected period. Each bar shows only the views gained during its day, week or hour.',
                      )
                    : t(
                        'Cumul des vues observées depuis le début de la période.',
                        'Running total of observed views since the period started.',
                      )
                  : t(
                      'Total des abonnés observés à chaque date, pas le gain journalier.',
                      'Total followers observed on each date, not the daily gain.',
                    )}
              </p>
              <p>{t('Dates et heures en UTC.', 'Dates and times in UTC.')}</p>
              {metric === 'followers' && latest && (
                <p>
                  {t('Dernière mesure :', 'Latest observation:')}{' '}
                  {formatAnalyticsBucket(latest.x, granularity, locale, true)}
                </p>
              )}
              {isPartial && (
                <p>
                  {t(
                    'La période en cours est partielle et peut encore évoluer.',
                    'The current bucket is partial and can still change.',
                  )}
                </p>
              )}
              {hasGaps && (
                <p>
                  {metric === 'views' && mode === 'cumulative'
                    ? t(
                        'La courbe relie les cumuls connus. Les intervalles sans relevé restent des données manquantes ; aucune vue n’est ajoutée pour ces intervalles.',
                        'The line connects known cumulative totals. Intervals without readings remain missing data; no views are added for those intervals.',
                      )
                    : t(
                        'Les interruptions indiquent des données manquantes ; elles ne sont pas remplacées par zéro.',
                        'Gaps indicate missing data; they are not replaced with zero.',
                      )}
                </p>
              )}
            </div>
          </details>
        </div>
      </Tile>
    </section>
  );
}

function mapTimeSeries(series: TimeSeries | undefined): AnalyticsSeriesPoint[] {
  return series?.points.map((point) => ({ t: point.timestamp, v: point.value })) ?? [];
}

function mapOverviewSeries(
  points: OverviewSeriesResponse['followers'] | undefined,
): AnalyticsSeriesPoint[] {
  return points?.map((point) => ({ t: point.timestamp ?? point.date ?? '', v: point.value })) ?? [];
}
